import { Component, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Artifact, ArtifactType, ArtifactXmlInfo, XmlArtifact } from '../models/artifact';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { JsonHttpService } from 'src/app/shared/http/json-http.service';
import { GenericModalService, ModalMultiButton, ModalSettingsBase, ModalSettingsConfirmMultiButton, ModalSettingsMessage, ModalSettingsTemplate } from '../../shared/generic-modal/generic-modal.service';
import { DeveloperArtifactService } from '../services/developer-artifact.service';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DateTimeService } from '../../shared/datetime/datetime.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { DxDataGridModule, DxFileUploaderModule } from 'devextreme-angular';
import { DxoEditingModule, DxoFormModule, DxoColumnFixingModule, DxoGroupPanelModule, DxiColumnModule, DxiValidationRuleModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { XmlArtifactGridComponent } from './xml-artifact-grid/xml-artifact-grid.component';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';
import { GridService } from '../../shared/grid/grid.service';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';

@Component({
    selector: 'app-developer-xml-artifacts',
    templateUrl: './developer-xml-artifacts.component.html',
    styleUrls: ['./developer-xml-artifacts.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoFormModule, DxoColumnFixingModule, DxoGroupPanelModule, DxiColumnModule, DxiValidationRuleModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxFileUploaderModule, XmlArtifactGridComponent]
})
export class DeveloperXmlArtifactsComponent implements OnInit {

    artifacts: XmlArtifact[] = [];
    selectedAssemblyInfo: ArtifactXmlInfo;
    allowUpdating = true;
    allowDeleting = true;
    allowAdding = true;
    allowShowLog = true;
    currentUploadedArtifactDataGuid: string;

    multipleUploadXmlFiles: XmlArtifact[] = [];
    @ViewChild('xmlMultiUploadTemplate', { static: true }) private xmlMultiUploadTemplate: TemplateRef<any>;
    @ViewChild(BizDataGridDirective, { static: true }) grid: BizDataGridDirective;

    constructor(
        private developerArtifactService: DeveloperArtifactService,
        private jsonHttpService: JsonHttpService,
        private modalService: GenericModalService,
        private loadingPanelService: LoadingPanelService,
        private genericModalService: GenericModalService,
        private toastService: ToastService,
        private gridService: GridService,
        public bizGridFiltervalues: BizGridFiltervalues,
        public dateTimeService: DateTimeService) {
        }

    ngOnInit(): void {
        void this.refresh();
    }

    async refresh() {
        this.artifacts = await this.developerArtifactService.getXmlArtifacts(true);
    }

    onInitNewRow(e: DxDataGridTypes.InitNewRowEvent<XmlArtifact>): void {
        e.data = {
            ArtifactId: null,
            Name: '',
            Description: '',
            Filename: '',
            XmlType: '',
            UploadFileGuid: null
        } as XmlArtifact;
    }

    onRowInserting = (e: DxDataGridTypes.RowInsertingEvent<XmlArtifact>): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data, item => this.save(item));
    };

    onRowUpdating = (e: DxDataGridTypes.RowUpdatingEvent<XmlArtifact>): void => {
        e.cancel = this.gridService.handleDataGridRow({ ...e.oldData, ...e.newData } as XmlArtifact, item => this.save(item), e.newData);
    };

    onRowRemoving = (e: DxDataGridTypes.RowRemovingEvent<XmlArtifact>): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data, item => this.deleteXmlArtifact(item));
    };

    onEditingStart(e: any): void {
        this.clearCurrentUploadedArtifactDataGuid();
    }

    onEditCanceled(e: any): void {
        this.clearCurrentUploadedArtifactDataGuid();
    }

    async downloadArtifactData(artifact: Artifact) {
        await this.developerArtifactService.downloadArtifact(artifact.ArtifactId, ArtifactType.Xml);
    }

    async save(artifact: XmlArtifact): Promise<XmlArtifact> {
        try{
            artifact.UploadFileGuid = this.currentUploadedArtifactDataGuid;
            const newArtifact = await this.developerArtifactService.saveXmlArtifact(artifact);
            this.toastService.showToast('Artifact saved...', ToastType.success);
            return newArtifact;
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to save artifact...', ToastType.error);
            return undefined;
        }
    }

    getUploadUrl() {
        return this.jsonHttpService.generateBackendUrl(`/api/developer/artifacts/upload-artifact-data`);
    }

    fileOnValueChanged(args: any, cellInfo: any): void {
        if (args.value.length > 0) {
            cellInfo.setValue(args.value[0].name);
        }
        else {
            cellInfo.setValue(null);
        }
    }

    fileOnBeforeSend(args: any, cellInfo: any): void {
        const request = args.request as XMLHttpRequest;
        const headers = this.jsonHttpService.generateHeaders();
        request.setRequestHeader('Authorization', headers.get('Authorization'));
    }

    fileOnUploaded(args: any, cellInfo: any) {
        if (args.request.status === 200) {
            const uploadGuid = JSON.parse(args.request.responseText);
            this.currentUploadedArtifactDataGuid = uploadGuid;

            setTimeout(() => {
                this.getTypesForUploadedAssembly(cellInfo);
            });
        }
    }

    async getTypesForUploadedAssembly(cellInfo: any) {
        const xmlInfo = await this.developerArtifactService.resolveXmlArtifactTypes(this.currentUploadedArtifactDataGuid);

        const settings = {} as ModalSettingsBase;
        settings.size = 'lg';
        settings.backdrop = 'static';
        // Make sure we are on top with this modal!
        settings.windowClass = 'ngb-modal-on-top';
        settings.backdropClass = 'ngb-modal-on-top-backdrop';

        if (xmlInfo) {

            if (this.artifacts.some(a => a.Filename === xmlInfo.Filename && a.ArtifactId !== cellInfo.key.ArtifactId)) {
                this.currentUploadedArtifactDataGuid = null;
                var messageSettings = new ModalSettingsMessage();
                Object.assign(messageSettings, settings);
                messageSettings.body = 'Another artifact with same filename already exists.';
                messageSettings.title = 'Assembly alread exists';
                this.modalService.message(messageSettings);
                return;
            }

            if (cellInfo.data.ArtifactId != null && cellInfo.data.Filename !== xmlInfo.Filename) {
                const confirmMultiButtonSettings = new ModalSettingsConfirmMultiButton();
                Object.assign(confirmMultiButtonSettings, settings);
                confirmMultiButtonSettings.body = 'The uploaded artifact has a different filename than the currently saved artifact.<br/>Are you sure you want to upload this artifact?';
                confirmMultiButtonSettings.title = 'Potential new artifact detected';
                confirmMultiButtonSettings.buttons = [];
                confirmMultiButtonSettings.buttons.push(new ModalMultiButton('Yes', null, 'btn btn-primary'));
                confirmMultiButtonSettings.buttons.push(new ModalMultiButton('No', 'cancelEdit', 'btn btn-link'));

                this.modalService.confirmMultiButton(confirmMultiButtonSettings).then(result => {
                    if (result === 'cancelEdit') {
                        this.currentUploadedArtifactDataGuid = null;
                        this.grid.instance.cancelEditData();
                        return;
                    }
                });
            }

            this.setXmlInfo(xmlInfo, cellInfo);
        }
        else {
            var messageSettings = new ModalSettingsMessage();
            messageSettings.body = 'No valid artifact-types found in uploaded file.';
            messageSettings.title = 'No valid types';
            this.modalService.message(messageSettings);
        }
    }

    async uploadMultiple() {
        await this.modalService.fileSelectorDirect('Select files', 'Select or drop the xml-files containing Schemas or XSLT.', true, 'Upload', 'Cancel', '.xsd,.xslt,.xsl').then(async files => {
            if (!files.every(f => f.name.endsWith('.xsd') || f.name.endsWith('.xsl') || f.name.endsWith('.xslt'))) {
                this.modalService.messageDirect('Invalid files', 'Only .xsd, .xsl and .xslt files are allowed.');
                return;
            }

            this.loadingPanelService.showLoadingPanelDirect('Checking artifacts...');
            const fileGuids = await this.developerArtifactService.uploadArtifacts(files);
            this.multipleUploadXmlFiles = await this.developerArtifactService.resolveMultipleXmlArtifactTypes(fileGuids);
            this.loadingPanelService.hideLoadingPanel();

            if (this.multipleUploadXmlFiles.length > 0) {
                const settings = new ModalSettingsTemplate();
                settings.size = 'lg';
                settings.backdrop = 'static';
                // Make sure we are on top with this modal!
                settings.windowClass = 'ngb-modal-on-top';
                settings.backdropClass = 'ngb-modal-on-top-backdrop';
                this.modalService.openTemplate(this.xmlMultiUploadTemplate, settings).then(() => {
                    this.loadingPanelService.showLoadingPanelDirect('Saving artifacts...');
                    this.developerArtifactService.saveXmlArtifacts(this.multipleUploadXmlFiles).then(() => {
                        this.loadingPanelService.hideLoadingPanel();
                        this.toastService.showToast('Artifacts saved successfully', ToastType.success);
                        this.refresh();
                    });
                }).catch(e => { });
            }
        }).catch(e => { });
    }

    async cancelDeleteXmlArtifact(xmlArtifact: XmlArtifact): Promise<boolean> {
        const allowDeleting = await this.developerArtifactService.allowDeletingXmlArtifact(xmlArtifact.ArtifactId);

        if(!allowDeleting){
            const settings = new ModalSettingsMessage();
            settings.size = 'lg';
            settings.backdrop = 'static';

            // Make sure we are on top with this modal!
            settings.windowClass = 'ngb-modal-on-top';
            settings.backdropClass = 'ngb-modal-on-top-backdrop';
            settings.title = 'Already in use';
            settings.body = 'It is not possible to delete the xml artifact, because it is currently in use.';
            this.genericModalService.message(settings);
            return true;
        }
        return false;
    }

    async deleteXmlArtifact(xmlArtifact: XmlArtifact): Promise<XmlArtifact> {
        try {
            await this.developerArtifactService.deleteXmlArtifact(xmlArtifact.ArtifactId, err => err?.status !== 409);
            this.toastService.showToast('Xml artifact deleted...', ToastType.success);
        }
        catch (err) {
            // Process 409 error here; other errors are processed using generic error handling
            if (err?.status === 409) {
                const settings = new ModalSettingsConfirmMultiButton('Delete failed', `Error details: <pre style='white-space: pre-wrap;'>${err.error}</pre>`);
                settings.size = 'lg';
                settings.backdrop = 'static';
                settings.buttons = [new ModalMultiButton('Close', null, 'btn btn-primary')];
                await this.genericModalService.confirmMultiButton(settings);
            }
        }
        return xmlArtifact;
    }

    setXmlInfo(xmlInfo: ArtifactXmlInfo, cellInfo: any) {
        cellInfo.component.cellValue(cellInfo.row.rowIndex, 'FileName', cellInfo.data.Filename);
        cellInfo.component.cellValue(cellInfo.row.rowIndex, 'XmlType', xmlInfo.XmlType);
    }

    selectedAssemblyInfoChanged(e: any) {
        if (e.selectedRowsData.length > 0) {
            this.selectedAssemblyInfo = e.selectedRowsData[0];
        }
    }

    getFilenameDisplayName(rowData: any) {
        return rowData.Filename; // This method is necessary for sorting/filtering/etc. when using cell-templates.
    }

    customizeItem(item: any) {
        if (item.dataField === 'DownloadDataField') { // Hide the 'Download' column when editing...
            item.visible = false;
        }
    }

    calculateIsNewColumnValue(rowData: XmlArtifact) {
        return !rowData.ArtifactId;
    }

    clearCurrentUploadedArtifactDataGuid(){
        this.currentUploadedArtifactDataGuid = null;
    }
}
